import { useMemo } from "react";
import type { ShareableScopeKind } from "../../../shared/rbac/permissions";
import { type ResolveScopeName, roleIdFromChipKey } from "../../lib/roleChip";
import { playClick } from "../../lib/sounds";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { RedactedScopeRow } from "../RedactedScopeRow";
import { Text } from "../Text";
import { OverflowBadges } from "./OverflowBadges";
import { openRoleModal } from "../globalModalStack";
import {
  ClickablePillBadge,
  DetailLabel,
  DetailList,
  DetailRow,
  DetailRowRight,
  NeutralIconBadge,
} from "./shared";
import { buildAccessRows, type RoleInfo } from "./lib/buildAccessRows";

type GrantInput = {
  id: string;
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

type AppInfo = {
  id: string;
  name: string;
  identifier: string;
};

type Props = {
  grants: readonly GrantInput[];
  rolesMap: ReadonlyMap<string, RoleInfo>;
  resolveScopeName: ResolveScopeName;
  apps: ReadonlyMap<string, AppInfo>;
  emptyLabel?: string;
  emptyDescription?: string;
  hasTenantRead?: {
    space?: boolean;
    connected_app?: boolean;
    skill?: boolean;
    network?: boolean;
  };
  onOpenResource: (scopeId: string, scopeKind: ShareableScopeKind) => void;
};

export function PrincipalAccessTable({
  grants,
  rolesMap,
  resolveScopeName,
  apps,
  emptyLabel = "No access",
  emptyDescription = "No spaces, connectors, or skills are accessible.",
  hasTenantRead,
  onOpenResource,
}: Props) {
  const { rows, hidden } = useMemo(
    () => buildAccessRows(grants, resolveScopeName, rolesMap),
    [grants, resolveScopeName, rolesMap]
  );

  const visibleHidden = {
    space: hasTenantRead?.space ? 0 : hidden.space,
    connected_app: hasTenantRead?.connected_app ? 0 : hidden.connected_app,
    skill: hasTenantRead?.skill ? 0 : hidden.skill,
    network: hasTenantRead?.network ? 0 : hidden.network,
  };

  const isEmpty =
    rows.length === 0 &&
    visibleHidden.space === 0 &&
    visibleHidden.connected_app === 0 &&
    visibleHidden.skill === 0 &&
    visibleHidden.network === 0;

  return (
    <DetailList>
      {isEmpty ? (
        <DetailRow>
          <DetailLabel>
            <Text variant="text-regular-md">{emptyLabel}</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {emptyDescription}
            </Text>
          </DetailLabel>
        </DetailRow>
      ) : (
        <>
          {rows.map((row) => {
            const app =
              row.scopeKind === "connected_app"
                ? apps.get(row.scopeId)
                : undefined;
            return (
              <DetailRow
                key={row.scopeId}
                data-interactive="true"
                onMouseDown={() => playClick()}
                onClick={() => onOpenResource(row.scopeId, row.scopeKind)}
              >
                {app ? (
                  <ConnectorLogoContainer
                    size={32}
                    borderRadius={8}
                    outline={false}
                    hoverBackground={false}
                  >
                    <ConnectorLogo
                      template={{ name: app.name, identifier: app.identifier }}
                      size={16}
                    />
                  </ConnectorLogoContainer>
                ) : (
                  <NeutralIconBadge
                    iconName={
                      row.scopeKind === "space"
                        ? "hash"
                        : row.scopeKind === "skill"
                          ? "graduation-cap"
                          : row.scopeKind === "network"
                            ? "earth"
                            : row.scopeKind === "service_account"
                              ? "bot"
                              : "api-key-custom"
                    }
                  />
                )}
                <DetailLabel>
                  <Text
                    variant="text-regular-md"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {row.name}
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {row.scopeKind === "space"
                      ? "Space"
                      : row.scopeKind === "skill"
                        ? "Skill"
                        : row.scopeKind === "network"
                          ? "Network"
                          : row.scopeKind === "service_account"
                            ? "Service account"
                            : "Connector"}
                  </Text>
                </DetailLabel>
                <DetailRowRight>
                  <OverflowBadges
                    items={row.chips}
                    keyFn={(chip) => chip.key}
                    renderBadge={(chip) => (
                      <ClickablePillBadge
                        icon={chip.icon}
                        onClick={() =>
                          openRoleModal(roleIdFromChipKey(chip.key))
                        }
                      >
                        {chip.label}
                      </ClickablePillBadge>
                    )}
                    overflowTooltip={(h) => h.map((c) => c.label).join(", ")}
                  />
                </DetailRowRight>
              </DetailRow>
            );
          })}
          <RedactedScopeRow count={visibleHidden.space} scopeKind="space" />
          <RedactedScopeRow
            count={visibleHidden.connected_app}
            scopeKind="connected_app"
          />
          <RedactedScopeRow count={visibleHidden.skill} scopeKind="skill" />
          <RedactedScopeRow count={visibleHidden.network} scopeKind="network" />
        </>
      )}
    </DetailList>
  );
}
